import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { useFavorites } from "@/hooks/useFavorites";

type MovieCardProps = {
  movie: Movie;
  compact?: boolean;
};

export default function MovieCard({ movie, compact = false }: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(movie.id);

  return (
    <article className="group relative flex flex-col">
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200/80 bg-background-100">
        <img
          src={movie.cover}
          alt={`${movie.title}（${movie.year}）电影海报`}
          title={`${movie.title} ${movie.year} ${movie.genres.join(" ")} 剧情介绍`}
          className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-95" />

        <span className="absolute left-2.5 top-2.5 flex items-center gap-1 rounded-md bg-background-950/70 px-1.5 py-0.5 font-label text-[11px] font-semibold text-secondary-300 backdrop-blur-sm">
          <i className="ri-star-fill text-[10px] text-primary-400"></i>
          {movie.rating.toFixed(1)}
        </span>

        <button
          type="button"
          onClick={() => toggleFavorite(movie.id)}
          aria-label={favorited ? `取消收藏 ${movie.title}` : `收藏 ${movie.title}`}
          className={`absolute right-2.5 top-2.5 z-20 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border backdrop-blur-sm transition-colors ${
            favorited
              ? "border-primary-500 bg-primary-600/90 text-background-50"
              : "border-background-200/60 bg-background-950/50 text-foreground-700 hover:border-primary-500 hover:text-primary-300"
          }`}
        >
          <i className={`${favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} text-sm`}></i>
        </button>

        <Link
          to={`/detail/${movie.id}`}
          aria-label={`查看 ${movie.title} 详情`}
          className="absolute inset-0 z-10"
        ></Link>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <span className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2 py-1 font-label text-[11px] font-medium text-background-50">
            剧情详情
            <i className="ri-arrow-right-line text-[11px]"></i>
          </span>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        <Link to={`/detail/${movie.id}`} className="block">
          <h3 className="truncate font-heading text-[15px] font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-300">
            {movie.title}
          </h3>
        </Link>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-label text-[11px] text-foreground-500">
          <span>{movie.year}</span>
          <span className="h-2.5 w-px bg-background-300"></span>
          <span>{movie.country}</span>
          {!compact && (
            <>
              <span className="h-2.5 w-px bg-background-300"></span>
              <span className="truncate">{movie.genres.join(" / ")}</span>
            </>
          )}
        </p>
      </div>
    </article>
  );
}